import type { Theme } from '@esign/contracts'
import { hexToOklch, oklchToHex } from './oklch.js'
import { ensureAccessible, pickTextOn } from './contrast.js'

/** Lightness mục tiêu cho từng bậc, và hệ số chroma (nhạt ở hai đầu, đậm ở giữa). */
const RAMP: ReadonlyArray<readonly [step: number, lightness: number, chromaScale: number]> = [
  [50, 0.97, 0.15],
  [100, 0.94, 0.28],
  [200, 0.88, 0.5],
  [300, 0.8, 0.72],
  [400, 0.7, 0.9],
  [500, 0.62, 1.0],
  [600, 0.54, 0.95],
  [700, 0.45, 0.85],
  [800, 0.37, 0.7],
  [900, 0.28, 0.55],
]

/** Token trung tính — cố định, không phụ thuộc partner. Khai báo semantic ngay từ đầu để thêm dark mode sau chỉ là một block CSS. */
const NEUTRAL = {
  surface: '#FFFFFF',
  'surface-2': '#F6F7F9',
  'surface-3': '#EDEFF3',
  text: '#111827',
  'text-muted': '#6B7280',
  'text-subtle': '#9CA3AF',
  border: '#E5E7EB',
  'border-strong': '#D1D5DB',
  danger: '#DC2626',
  'danger-surface': '#FEF2F2',
  success: '#16A34A',
  'success-surface': '#F0FDF4',
  warning: '#D97706',
  'warning-surface': '#FFFBEB',
} as const

export type Palette = {
  tokens: Record<string, string>
  /** true khi contrast guard phải chỉnh màu brand — admin hiện cảnh báo dựa vào cờ này. */
  contrastAdjusted: boolean
  brandRamp: Array<{ step: number; hex: string }>
}

/**
 * Từ ĐÚNG MỘT màu brand → toàn bộ palette + token ngữ nghĩa.
 *
 * Partner chỉ phải khai một màu. Bắt họ khai 10 bậc là bắt họ làm việc của designer,
 * và sẽ nhận về 10 bậc không nhất quán.
 */
export function derivePalette(theme: Theme): Palette {
  const accessible = ensureAccessible(theme.brand_color)
  const primary = accessible.primary
  const onPrimary = theme.on_primary || accessible.onPrimary
  const adjusted = accessible.adjusted
  const base = hexToOklch(theme.brand_color)

  const brandRamp = RAMP.map(([step, l, chromaScale]) => ({
    step,
    hex: oklchToHex({ l, c: base.c * chromaScale, h: base.h }),
  }))

  const secondaryColor = theme.secondary_color || '#EE46BC'
  const secondaryBase = hexToOklch(secondaryColor)
  const secondaryRamp = RAMP.map(([step, l, chromaScale]) => ({
    step,
    hex: oklchToHex({ l, c: secondaryBase.c * chromaScale, h: secondaryBase.h }),
  }))

  const tokens: Record<string, string> = {
    ...Object.fromEntries(brandRamp.map(({ step, hex }) => [`brand-${step}`, hex])),
    ...Object.fromEntries(secondaryRamp.map(({ step, hex }) => [`secondary-${step}`, hex])),
    ...NEUTRAL,
    brand: theme.brand_color,
    primary,
    'primary-hover': oklchToHex({ ...hexToOklch(primary), l: hexToOklch(primary).l - 0.05 }),
    'on-primary': onPrimary,
    'primary-surface': brandRamp[0]!.hex,
    'primary-border': brandRamp[2]!.hex,
    'focus-ring': primary,
    secondary: secondaryColor,
    'secondary-hover': oklchToHex({ ...hexToOklch(secondaryColor), l: hexToOklch(secondaryColor).l - 0.05 }),
    'on-secondary': '#FFFFFF',
    radius: theme.radius,
    'radius-sm': `${Math.max(0, parseInt(theme.radius, 10) - 4)}px`,
    'radius-lg': `${parseInt(theme.radius, 10) + 4}px`,
  }

  tokens['on-primary-surface'] = pickTextOn(tokens['primary-surface']!)

  return { tokens, contrastAdjusted: adjusted, brandRamp }
}
